// ============================================================================
// `@torre/payroll` — contrato compartido del módulo "Asistencia, Anticipos y
// Nómina" (issues AgroCloud#36 asistencia · AgroCloud#37 anticipos y otros descuentos/haberes ·
// AgroCloud#38 liquidación de sueldo · AgroCloud#81 autorización de incidencias del equipo ·
// AgroCloud#82 cumplimiento fiscal CFDI/timbrado · AgroCloud#83 control de presencia biométrico).
//
// Paquete PURO y tenant-agnóstico: sin `process.env`, sin `import.meta.env`,
// sin `axios`, sin `firebase` y sin triggers. Solo datos + funciones puras, de
// modo que `apps/v5` y `torre-de-control-api` compartan una única fuente de
// verdad y la liquidación se pueda testear sin Firestore.
//
// Contenido:
//   • COLECCIONES  — nombres de colección Firestore
//   • VOCABULARIOS — estados de asistencia, tipos de movimiento, incidencias…
//   • TIPOS        — AttendanceRecord, PayrollAdjustment, WorkLog,
//                    ContributionParam, PayrollPeriod, Payslip…
//   • ASISTENCIA   — jornadas y horas trabajadas (AgroCloud#36)
//   • MOVIMIENTOS  — anticipos, préstamos en cuotas y otros haberes (AgroCloud#37)
//   • INCIDENCIAS  — máquina de estados capturada→autorizada→nómina (AgroCloud#81)
//   • PREVISIONAL  — descuentos y aportes sobre la base imponible (AgroCloud#38)
//   • LIQUIDACIÓN  — armado de haberes/descuentos y líquido a pagar (AgroCloud#38)
//   • CFDI         — comprobante de nómina + PUERTO de timbrado (AgroCloud#82)
//   • MARCAJE      — contrato del reloj checador / biometría (AgroCloud#83, sin implementar)
//   • VALIDADORES  — validación pura compartida front/back
//   • SERVICIO     — PAYROLL_ENDPOINTS (contrato de rutas planas)
//
// ---------------------------------------------------------------------------
// DOS DECISIONES DE DISEÑO QUE NO SON NEGOCIABLES — ESTO ES SUELDO DE GENTE
// ---------------------------------------------------------------------------
//
// 1) SOBRE EL PAGO DE la tarea (AgroCloud#38 → AgroCloud#13): el cálculo del pago a entregable/día
//    YA existe en `@torre/team` (`computeSettlementPayment`, issue AgroCloud#13 /
//    PR AgroCloud#100). Este paquete **no lo reimplementa**: expone un *puerto*
//    (`ComputePaymentFn`) que se inyecta. Si no se inyecta, la línea de sueldo
//    queda con `amount: null` y su porqué — NUNCA con un 0.
//
// 2) SOBRE EL `null` (AgroCloud#38): un importe desconocido se representa con `null`,
//    jamás con `0`. "No sé cuánto se le debe" y "no se le debe nada" son cosas
//    distintas, y confundirlas es pagar mal un sueldo. Si CUALQUIER línea de la
//    liquidación no se pudo calcular, `netPay` es `null` y `complete` es
//    `false`: la liquidación no se puede pagar hasta que se resuelva. La UI lo
//    dice explícitamente en vez de mostrar una cifra falsa.
// ============================================================================

// ---------------------------------------------------------------------------
// COLECCIONES (Firestore)
// ---------------------------------------------------------------------------

/** Registro de asistencia diaria por colaborador (AgroCloud#36). */
export const PAYROLL_ATTENDANCE_COLLECTION = "payroll_attendance";
/** Anticipos, préstamos y otros haberes/descuentos (AgroCloud#37). */
export const PAYROLL_ADJUSTMENTS_COLLECTION = "payroll_adjustments";
/** Incidencias capturadas por el equipo, pendientes de autorización (AgroCloud#81). */
export const PAYROLL_WORK_LOGS_COLLECTION = "payroll_work_logs";
/** Parámetros previsionales (entidades de pensión/salud/impuesto) (AgroCloud#38). */
export const PAYROLL_CONTRIBUTION_COLLECTION = "payroll_contributions";
/** Periodos de nómina (semanal/quincenal/…) y su cierre (AgroCloud#82). */
export const PAYROLL_PERIODS_COLLECTION = "payroll_periods";
/** Liquidaciones de sueldo emitidas (AgroCloud#38). Entidad principal del módulo. */
export const PAYSLIPS_COLLECTION = "payslips";

/**
 * Colecciones del módulo Equipo (AgroCloud#11/#13, PR AgroCloud#100). La nómina solo las
 * LEE (maestro de colaboradores y tarifas de pago); su administración vive allá.
 */
export const TEAM_MEMBERS_COLLECTION = "team_members";
export const PAY_RATES_COLLECTION = "pay_rates";

// ---------------------------------------------------------------------------
// VOCABULARIOS — ASISTENCIA (AgroCloud#36)
// ---------------------------------------------------------------------------

/**
 * Estado de la jornada de un colaborador en una fecha (AgroCloud#36).
 * `half_day` existe porque por el equipo se paga medio día con frecuencia.
 */
export const ATTENDANCE_STATUSES = [
  "present",
  "half_day",
  "absent",
  "leave",
  "sick_leave",
  "vacation",
  "holiday",
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

/**
 * Jornadas **trabajadas** que aporta cada estado (AgroCloud#36). Solo la presencia real
 * suma jornada trabajada.
 *
 * ⚠️ DEUDA CONSCIENTE: si `leave`, `sickLeave`, `vacation` y `holiday` se
 * pagan (y a qué tasa) es una regla **jurisdiccional** que este módulo NO
 * decide. Aquí valen 0 jornadas trabajadas; el haber correspondiente, cuando
 * exista la política del país, se captura hoy como un haber manual o un
 * movimiento (AgroCloud#37). No se inventa una regla de pago que nadie aprobó.
 */
export const DAYS_BY_STATUS: Record<AttendanceStatus, number> = {
  present: 1,
  half_day: 0.5,
  absent: 0,
  leave: 0,
  sick_leave: 0,
  vacation: 0,
  holiday: 0,
};

/** Estados que representan presencia efectiva del colaborador (AgroCloud#36). */
export const PRESENT_ATTENDANCE_STATUSES: readonly AttendanceStatus[] = [
  "present",
  "half_day",
];

// ---------------------------------------------------------------------------
// VOCABULARIOS — MARCAJE / reloj checador (AgroCloud#83)
// ---------------------------------------------------------------------------

/**
 * De dónde salió el marcaje de asistencia (AgroCloud#36 / AgroCloud#83).
 *
 * ⚠️ HONESTIDAD DE ALCANCE: hoy **solo `manual` está implementado**. La issue
 * AgroCloud#83 (biometría facial/huella/NFC y reloj checador) está etiquetada
 * `alcance-a-evaluar` y requiere hardware: aquí se declara el vocabulario y el
 * contrato de ingesta (`TimeClockPunch`) para que el día que se apruebe el
 * hardware el modelo de datos ya exista, pero **no hay ninguna integración
 * biométrica real**. Ver `IMPLEMENTED_CLOCK_SOURCES` y `isImplementedSource`.
 */
export const CLOCK_SOURCES = [
  "manual",
  "time_clock",
  "face_biometric",
  "fingerprint_biometric",
  "nfc",
  "mobile_app",
] as const;
export type ClockSource = (typeof CLOCK_SOURCES)[number];

/** Fuentes de marcaje realmente implementadas hoy (AgroCloud#83 = maqueta). */
export const IMPLEMENTED_CLOCK_SOURCES: readonly ClockSource[] = ["manual"];

/** ¿Esta fuente de marcaje existe de verdad, o es una promesa de roadmap? */
export function isImplementedSource(source?: string | null): boolean {
  return IMPLEMENTED_CLOCK_SOURCES.includes(source as ClockSource);
}

// ---------------------------------------------------------------------------
// VOCABULARIOS — MOVIMIENTOS (AgroCloud#37)
// ---------------------------------------------------------------------------

/** Tipos de haber/descuento que afectan la liquidación (AgroCloud#37). */
export const ADJUSTMENT_TYPES = [
  "advance",
  "loan",
  "deduction",
  "earning",
  "bonus",
] as const;
export type AdjustmentType = (typeof ADJUSTMENT_TYPES)[number];

/** Signo con el que cada tipo entra en la liquidación: −1 resta, +1 suma (AgroCloud#37). */
export const ADJUSTMENT_SIGN: Record<AdjustmentType, -1 | 1> = {
  advance: -1,
  loan: -1,
  deduction: -1,
  earning: 1,
  bonus: 1,
};

/** Ciclo de vida de un movimiento (AgroCloud#37). */
export const ADJUSTMENT_STATUSES = ["pending", "applied", "void"] as const;
export type AdjustmentStatus = (typeof ADJUSTMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// VOCABULARIOS — incidencias del equipo (AgroCloud#81)
// ---------------------------------------------------------------------------

/** Qué reporta una incidencia del equipo (AgroCloud#81). */
export const WORK_LOG_TYPES = [
  "attendance",
  "deliverable",
  "overtime",
  "bonus",
  "deduction",
] as const;
export type WorkLogType = (typeof WORK_LOG_TYPES)[number];

/** Flujo de autorización campo → supervisor → nómina (AgroCloud#81). */
export const WORK_LOG_STATUSES = [
  "submitted",
  "approved",
  "rejected",
  "sent_to_payroll",
] as const;
export type WorkLogStatus = (typeof WORK_LOG_STATUSES)[number];

/**
 * Transiciones permitidas del flujo de autorización (AgroCloud#81). Cualquier otra es un
 * error: el criterio de la historia es que **solo lo autorizado llegue a nómina**.
 */
export const WORK_LOG_TRANSITIONS: Record<WorkLogStatus, readonly WorkLogStatus[]> = {
  submitted: ["approved", "rejected"],
  approved: ["sent_to_payroll", "rejected"],
  rejected: ["submitted"],
  // Terminal: una incidencia ya enviada a nómina no se re-abre; se corrige con
  // un movimiento de ajuste (AgroCloud#37), que deja rastro.
  sent_to_payroll: [],
};

/**
 * Estados de incidencia que la liquidación puede consumir (AgroCloud#81).
 * `submitted` y `rejected` NO están aquí, y ese es el corazón de la historia.
 */
export const PAYABLE_WORK_LOG_STATUSES: readonly WorkLogStatus[] = [
  "approved",
  "sent_to_payroll",
];

/** Origen de la captura: consola web o app móvil por el equipo (AgroCloud#81). */
export const WORK_LOG_ORIGINS = ["web", "app"] as const;
export type WorkLogOrigin = (typeof WORK_LOG_ORIGINS)[number];

// ---------------------------------------------------------------------------
// VOCABULARIOS — PREVISIONAL, PERIODOS Y LIQUIDACIÓN (AgroCloud#38 / AgroCloud#82)
// ---------------------------------------------------------------------------

/** Naturaleza de un parámetro previsional (AgroCloud#38). */
export const CONTRIBUTION_TYPES = [
  "pension",
  "health",
  "unemployment_insurance",
  "tax",
  "other",
] as const;
export type ContributionType = (typeof CONTRIBUTION_TYPES)[number];

/**
 * Periodicidad del periodo de nómina (AgroCloud#82). Espeja el catálogo SAT
 * `c_pay_frequency` para que el CFDI de nómina mexicano pueda mapearse.
 */
export const PAY_FREQUENCIES = [
  "daily",
  "weekly",
  "biweekly",
  "semimonthly",
  "monthly",
  "bimonthly",
  "per_deliverable",
  "other",
] as const;
export type PayFrequency = (typeof PAY_FREQUENCIES)[number];

/** Tipo de nómina (AgroCloud#82) — espeja el catálogo SAT `c_payroll_type`. */
export const PAYROLL_TYPES = ["regular", "extraordinary"] as const;
export type PayrollType = (typeof PAYROLL_TYPES)[number];

/** Ciclo de vida de un periodo de nómina (AgroCloud#82). */
export const PERIOD_STATUSES = ["open", "calculated", "closed"] as const;
export type PeriodStatus = (typeof PERIOD_STATUSES)[number];

/** Ciclo de vida de una liquidación de sueldo (AgroCloud#38). */
export const PAYSLIP_STATUSES = [
  "draft",
  "calculated",
  "approved",
  "paid",
  "void",
] as const;
export type PayslipStatus = (typeof PAYSLIP_STATUSES)[number];

/** De dónde salió una línea de la liquidación (trazabilidad, AgroCloud#38). */
export const LINE_SOURCES = [
  "attendance",
  "work_log",
  "adjustment",
  "contribution",
  "manual",
] as const;
export type LineSource = (typeof LINE_SOURCES)[number];

/**
 * Por qué una línea puede quedar sin monto (AgroCloud#38). Cada valor distinto de `ok`
 * significa "no lo sé", y bloquea el pago de la liquidación completa.
 */
export const LINE_STATUSES = [
  "ok",
  "no_rate",
  "no_daily_rate",
  // Colaboradores (AgroCloud#100) respondió pero NO pudo calcular el importe (p. ej. la
  // tarifa no cubre esos entregables). Su `noCalcReason` viaja en la línea.
  "no_payment_calc",
  "team_module_missing",
  // Sueldo base por hora sin tarifa por hora, o sueldo fijo sin sueldo (#530).
  "no_hourly_rate",
  "no_salary",
  // La base imponible es desconocida porque algún haber quedó sin monto. Es un
  // motivo DERIVADO (el problema real está en la línea de arriba), por eso su
  // severidad es baja: marcarlo como `team_module_missing` mandaba al usuario a
  // buscar el problema al módulo Equipo aunque el módulo estuviera perfecto.
  "no_taxable_base",
] as const;
export type LineStatus = (typeof LINE_STATUSES)[number];

// ---------------------------------------------------------------------------
// VOCABULARIOS — TIMBRADO CFDI (AgroCloud#82)
// ---------------------------------------------------------------------------

/**
 * Estado fiscal del comprobante de nómina (AgroCloud#82).
 *
 * ⚠️ `no_pac_configured` es el estado REAL hoy: timbrar ante el SAT exige un
 * PAC (Proveedor Autorizado de Certificación) contratado, con certificado de
 * sello digital y credenciales. No hay ninguno configurado en este repo y este
 * módulo **NO simula un timbrado**: no inventa UUID, ni sello, ni cadena
 * original. Un CFDI falso es un delito fiscal, no un placeholder.
 */
export const STAMP_STATUSES = [
  "not_stamped",
  "pending",
  "stamped",
  "error",
  "no_pac_configured",
] as const;
export type StampStatus = (typeof STAMP_STATUSES)[number];

// ---------------------------------------------------------------------------
// PUERTOS — lo que este módulo delega en otros (nunca reimplementa)
// ---------------------------------------------------------------------------

/**
 * Forma mínima de una tarifa de pago de `@torre/team` (AgroCloud#13). Se declara
 * estructuralmente para NO acoplar este paquete al otro por `package.json`: el
 * módulo Equipo todavía no está en `master` (PR AgroCloud#100) y declarar
 * `"@torre/team": "workspace:*"` rompería `pnpm install`.
 */
export interface PayRateLike {
  name?: string;
  mode?: string;
  pricingType?: string;
  unitPrice?: number | null;
  tiers?: { from: number; to?: number | null; price: number }[];
  dailyRate?: number | null;
  hourlyRate?: number | null;
  fixedSalary?: number | null;
  agreedAmount?: number | null;
  bonusMinOutput?: number | null;
  bonusAmount?: number | null;
  outputUnit?: string | null;
  currency?: string;
}

/**
 * Resultado del cálculo de pago de `@torre/team` (AgroCloud#13 / AgroCloud#100).
 *
 * ⚠️ CONTRATO AgroCloud#100 (tras su revisión): cuando NO se puede calcular el pago,
 * los importes vienen en `null` (nunca 0) y `noCalcReason` explica por qué.
 * `total: null` significa "no sé cuánto se le debe" — la nómina NO lo convierte
 * en 0 (ver `amountFromPayment`), lo trata como línea incompleta.
 */
export interface PaymentCalculationLike {
  mode: string;
  appliedUnitPrice: number | null;
  subtotal: number | null;
  bonus: number;
  total: number | null;
  noCalcReason?: string | null;
}

/**
 * **Puerto de pago** (AgroCloud#38 → AgroCloud#13): la firma de `computeSettlementPayment` de
 * `@torre/team`. La nómina la recibe inyectada y la llama; jamás
 * reimplementa la fórmula de entregable/día.
 */
export type ComputePaymentFn = (
  rate: PayRateLike,
  input: {
    output?: number | null;
    days?: number | null;
    hours?: number | null;
    /** % de avance de un pago por proyecto (AgroCloud#13). */
    progressPercentage?: number | null;
    /** Lo ya pagado de ese proyecto en liquidaciones anteriores (AgroCloud#13). */
    previouslyPaid?: number | null;
    periods?: number | null;
    manualBonus?: number | null;
    mode?: string;
  },
) => PaymentCalculationLike;

// ---------------------------------------------------------------------------
// TIPOS — Asistencia (AgroCloud#36)
// ---------------------------------------------------------------------------

export interface AttendanceRecord {
  id: string;
  /** Fecha de la jornada (ISO YYYY-MM-DD). */
  date: string;
  teamMemberId: string;
  /** Nombre denormalizado para listados. */
  teamMemberName?: string | null;
  status?: AttendanceStatus;
  /** Marcaje de entrada "HH:MM". */
  checkIn?: string | null;
  /** Marcaje de salida "HH:MM". */
  checkOut?: string | null;
  /** Horas trabajadas derivadas de los marcajes. */
  hoursWorked?: number | null;
  /** Horas extra reportadas (se pagan vía incidencia/movimiento). */
  overtime?: number | null;
  /**
   * Jornadas trabajadas. Si viene explícita manda sobre `DAYS_BY_STATUS`
   * (permite el caso real "trabajó 0,75 de jornada").
   */
  days?: number | null;
  /** Sitio de la operación multi-sitio: oficina, remoto, cliente (AgroCloud#81). */
  locationId?: string | null;
  locationName?: string | null;
  projectId?: string | null;
  costCenterId?: string | null;
  /** De dónde salió el marcaje (AgroCloud#83). Hoy siempre `manual`. */
  source?: ClockSource;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Consolidado de asistencia de un colaborador en un periodo (AgroCloud#36). */
export interface AttendanceSummary {
  teamMemberId: string;
  teamMemberName?: string | null;
  /** Días con algún registro. */
  records: number;
  /** Jornadas efectivamente trabajadas (base del pago por día). */
  daysWorked: number;
  /** Días ausentes (sin justificar). */
  absences: number;
  /** Conteo por estado, para que nada quede escondido. */
  byStatus: Record<string, number>;
  hoursWorked: number;
  overtime: number;
}

// ---------------------------------------------------------------------------
// TIPOS — Movimientos: anticipos y otros haberes/descuentos (AgroCloud#37)
// ---------------------------------------------------------------------------

export interface PayrollAdjustment {
  id: string;
  date: string;
  teamMemberId: string;
  teamMemberName?: string | null;
  type: AdjustmentType;
  /** Glosa que verá el colaborador en su liquidación. */
  concept: string;
  /** Monto total del movimiento, siempre positivo. El signo lo da el tipo. */
  amount: number;
  /**
   * Préstamo a pagar en N cuotas (AgroCloud#37). `null`/1 = se descuenta de una vez.
   * Solo aplica a `type: "loan"`.
   */
  installments?: number | null;
  /** Cuotas ya descontadas en liquidaciones anteriores. */
  paidInstallments?: number | null;
  /**
   * ¿El haber es imponible (paga previsional)? Por defecto los haberes SÍ lo
   * son. Irrelevante para los descuentos.
   */
  taxable?: boolean;
  /** Periodo de nómina al que se imputa; `null` = al próximo que lo tome. */
  periodId?: string | null;
  status?: AdjustmentStatus;
  /** Liquidación que finalmente lo aplicó (trazabilidad). */
  payslipId?: string | null;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Incidencias del equipo (AgroCloud#81)
// ---------------------------------------------------------------------------

export interface WorkLog {
  id: string;
  date: string;
  teamMemberId: string;
  teamMemberName?: string | null;
  squadId?: string | null;
  squadName?: string | null;
  /** Operación multi-sitio: oficina, remoto, cliente (AgroCloud#81). */
  locationId?: string | null;
  locationName?: string | null;
  type: WorkLogType;
  concept?: string | null;
  /**
   * Monto autorizado explícitamente por el supervisor. Si viene, manda: el
   * supervisor autorizó ESA cifra y no hay nada que recalcular.
   */
  amount?: number | null;
  /** Tarifa de pago (AgroCloud#13) para el entregable; el pago se DELEGA, no se calcula. */
  payRateId?: string | null;
  payRateName?: string | null;
  /** Entregables completados (entregable). */
  output?: number | null;
  /** Jornadas reportadas (incidencia de asistencia). */
  days?: number | null;
  /** Horas extra reportadas. */
  hours?: number | null;
  /** % de avance cuando la tarifa se paga por proyecto (AgroCloud#13). */
  progressPercentage?: number | null;
  /** Lo ya pagado de ese proyecto en liquidaciones anteriores (AgroCloud#13). */
  previouslyPaid?: number | null;
  /** Periodos cubiertos cuando la tarifa es de sueldo fijo (AgroCloud#13). */
  periods?: number | null;
  status?: WorkLogStatus;
  /** Quién autorizó/rechazó y cuándo (auditoría del flujo AgroCloud#81). */
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
  /** Periodo de nómina al que se envió (AgroCloud#81 → AgroCloud#38). */
  periodId?: string | null;
  /** Liquidación que la consumió. */
  payslipId?: string | null;
  origin?: WorkLogOrigin;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Parámetros previsionales (AgroCloud#38)
// ---------------------------------------------------------------------------

export interface ContributionParam {
  id: string;
  /** Nombre de la entidad o del concepto (Afore Habitat, IMSS, ISR…). */
  name: string;
  type: ContributionType;
  /** % que se le descuenta al colaborador sobre la base imponible (0–100). */
  employeePercentage?: number | null;
  /** % que aporta el empleador (no se descuenta del líquido). */
  employerPercentage?: number | null;
  /** Tope de base imponible; `null` = sin tope. */
  taxableCap?: number | null;
  active?: boolean;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Un descuento previsional ya calculado sobre una base concreta (AgroCloud#38). */
export interface CalculatedContributionDeduction {
  paramId: string;
  name: string;
  type: ContributionType;
  /** Base efectivamente usada (imponible topado, si había tope). */
  base: number;
  percentage: number;
  amount: number;
}

// ---------------------------------------------------------------------------
// TIPOS — Periodo de nómina (AgroCloud#82)
// ---------------------------------------------------------------------------

export interface PayrollPeriod {
  id: string;
  name: string;
  frequency?: PayFrequency;
  payrollType?: PayrollType;
  /** Inicio del periodo (ISO YYYY-MM-DD). */
  startDate: string;
  /** Fin del periodo (ISO YYYY-MM-DD), inclusive. */
  endDate: string;
  /** Fecha de pago efectivo. */
  payDate?: string | null;
  status?: PeriodStatus;
  /** Folio interno del periodo (AgroCloud#82). */
  folio?: string | null;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// TIPOS — Liquidación de sueldo (AgroCloud#38)
// ---------------------------------------------------------------------------

export interface PayslipLine {
  id: string;
  type: "earning" | "deduction";
  /** Glosa que verá el colaborador. */
  concept: string;
  /**
   * Importe de la línea, SIEMPRE positivo (el signo lo da `type`).
   * `null` = no se pudo calcular. Ver `status` para el porqué. NUNCA 0 por
   * ignorancia: un 0 aquí sería decirle a un colaborador que no se le debe nada.
   */
  amount: number | null;
  /** ¿Este haber paga previsional? Irrelevante en los descuentos. */
  taxable?: boolean;
  origin: LineSource;
  /** Documento que originó la línea (registro, incidencia, movimiento…). */
  referenceId?: string | null;
  status?: LineStatus;
  /**
   * Cuando `status === "no_payment_calc"`: el motivo que devolvió Colaboradores
   * (AgroCloud#100) en `noCalcReason` (p. ej. `no_price_for_output`). Sube a
   * la UI para decirle al usuario exactamente qué corregir. `null` si no aplica.
   */
  noCalcReason?: string | null;
}

/** Resultado del cálculo completo de una liquidación de sueldo (AgroCloud#38). */
export interface PayslipCalculation {
  lines: PayslipLine[];
  /** Jornadas trabajadas según la asistencia del periodo (AgroCloud#36). */
  daysWorked: number;
  /** Suma de haberes; `null` si alguno es desconocido. */
  totalEarnings: number | null;
  /** Base sobre la que se calcula lo previsional; `null` si es desconocida. */
  taxableTotal: number | null;
  /** Suma de descuentos (previsionales incluidos); `null` si alguno es desconocido. */
  totalDeductions: number | null;
  /** Solo la parte previsional de los descuentos. */
  totalContributions: number;
  /** Aporte del empleador (no se descuenta al colaborador; es costo empresa). */
  totalEmployerContribution: number;
  /** Haberes − descuentos. `null` si la liquidación está incompleta. */
  netPay: number | null;
  /** `false` si alguna línea quedó sin monto → NO se puede pagar. */
  complete: boolean;
  /** El motivo más grave por el que la liquidación está incompleta. */
  paymentStatus: LineStatus;
}

export interface Payslip {
  id: string;
  /** Periodo de nómina al que pertenece (AgroCloud#82). */
  periodId?: string | null;
  periodName?: string | null;
  teamMemberId: string;
  teamMemberName?: string | null;
  /** Valor de la jornada usado en el cálculo (trazabilidad). */
  dailyRate?: number | null;
  /** Snapshot del cálculo, persistido para que el comprobante sea auditable. */
  calculation?: PayslipCalculation | null;
  status?: PayslipStatus;
  /** Estado fiscal del comprobante (AgroCloud#82). */
  stamped?: StampResult | null;
  currency?: string;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// HELPERS numéricos
// ---------------------------------------------------------------------------

/** Redondeo a 2 decimales — el único redondeo de dinero del módulo. */
const round2 = (n: number): number => Math.round((Number(n) || 0) * 100) / 100;

const number = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Número finito y > 0, o `null`. */
const positive = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const isNonEmptyText = (v: unknown): boolean =>
  typeof v === "string" && v.trim().length > 0;

/**
 * Suma una lista de importes que pueden ser desconocidos. Si CUALQUIERA es
 * `null`, el total es `null`: un total que ignora sumandos desconocidos es una
 * mentira con formato de número.
 */
export function strictSum(values: (number | null | undefined)[]): number | null {
  let acc = 0;
  for (const v of values) {
    if (v === null || v === undefined) return null;
    acc += number(v);
  }
  return round2(acc);
}

// ---------------------------------------------------------------------------
// ASISTENCIA (AgroCloud#36)
// ---------------------------------------------------------------------------

/** ¿La fecha ISO cae dentro de [desde, hasta] (ambos inclusive)? */
export function inTier(
  date?: string | null,
  from?: string | null,
  to?: string | null,
): boolean {
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

/** Convierte "HH:MM" a minutos desde medianoche; `null` si no es una hora. */
export function hourToMinutes(hour?: string | null): number | null {
  if (!hour) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hour).trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/**
 * Horas trabajadas entre dos marcajes (AgroCloud#36). Si la salida es anterior a la
 * entrada se asume turno que cruza medianoche. `null` si falta un marcaje o no
 * es válido — no se inventa una jornada.
 */
export function computeHoursWorked(
  checkIn?: string | null,
  checkOut?: string | null,
): number | null {
  const clockIn = hourToMinutes(checkIn);
  const clockOut = hourToMinutes(checkOut);
  if (clockIn === null || clockOut === null) return null;
  const minutes = clockOut >= clockIn ? clockOut - clockIn : 24 * 60 - clockIn + clockOut;
  return round2(minutes / 60);
}

/**
 * Jornadas trabajadas que aporta un registro de asistencia (AgroCloud#36). La jornada
 * explícita manda sobre el mapa por estado (caso real: "trabajó 0,75 de día").
 */
export function computeRecordDays(
  record: Pick<AttendanceRecord, "status" | "days">,
): number {
  if (record.days !== null && record.days !== undefined) {
    const j = Number(record.days);
    if (Number.isFinite(j) && j >= 0) return j;
  }
  const status = record.status ?? "present";
  return DAYS_BY_STATUS[status] ?? 0;
}

/**
 * Consolida la asistencia de UN colaborador en un periodo (AgroCloud#36). Es la base de
 * la liquidación por día (AgroCloud#38).
 */
export function computeAttendanceSummary(
  records: AttendanceRecord[] = [],
  from?: string | null,
  to?: string | null,
): AttendanceSummary[] {
  const groups = new Map<string, AttendanceSummary>();

  for (const r of records) {
    if ((from || to) && !inTier(r.date, from, to)) continue;
    if (!r.teamMemberId) continue;

    let g = groups.get(r.teamMemberId);
    if (!g) {
      g = {
        teamMemberId: r.teamMemberId,
        teamMemberName: r.teamMemberName ?? null,
        records: 0,
        daysWorked: 0,
        absences: 0,
        byStatus: {},
        hoursWorked: 0,
        overtime: 0,
      };
      groups.set(r.teamMemberId, g);
    }

    const status = r.status ?? "present";
    g.records += 1;
    g.byStatus[status] = (g.byStatus[status] ?? 0) + 1;
    if (status === "absent") g.absences += 1;
    g.daysWorked = round2(g.daysWorked + computeRecordDays(r));

    g.hoursWorked = round2(g.hoursWorked + recordHours(r));
    g.overtime = round2(g.overtime + number(r.overtime));
  }

  return [...groups.values()].sort((a, b) => b.daysWorked - a.daysWorked);
}

/** Jornadas trabajadas por un colaborador en un periodo (AgroCloud#36 → AgroCloud#38). */
export function computePeriodDays(
  records: AttendanceRecord[] = [],
  teamMemberId: string,
  from?: string | null,
  to?: string | null,
): number {
  return round2(
    records
      .filter((r) => r.teamMemberId === teamMemberId)
      .filter((r) => !(from || to) || inTier(r.date, from, to))
      .reduce((acc, r) => acc + computeRecordDays(r), 0),
  );
}

/**
 * Horas trabajadas de un colaborador en el periodo (#530): suma `hoursWorked`
 * de su asistencia (incluida la que genera el registro de horas por commits, #528).
 *
 * Si el registro no trae `hoursWorked` las deriva de los marcajes, EXACTAMENTE
 * igual que `computeAttendanceSummary`: si el resumen muestra 9 h y la nómina 0,
 * el sueldo por hora se pagaría en 0 mientras la pantalla dice otra cosa.
 */
export function computePeriodHours(
  records: AttendanceRecord[] = [],
  teamMemberId: string,
  from?: string | null,
  to?: string | null,
): number {
  return round2(
    records
      .filter((r) => r.teamMemberId === teamMemberId)
      .filter((r) => !(from || to) || inTier(r.date, from, to))
      .reduce((acc, r) => acc + recordHours(r), 0),
  );
}

/**
 * Horas de UN registro de asistencia (AgroCloud#36): las declaradas o, si no vienen, las
 * derivadas de entrada/salida. Única fuente de horas del módulo.
 */
function recordHours(record: AttendanceRecord): number {
  if (record.hoursWorked !== null && record.hoursWorked !== undefined) {
    return number(record.hoursWorked);
  }
  return computeHoursWorked(record.checkIn, record.checkOut) ?? 0;
}

/**
 * ¿Ya existe un registro de asistencia de ese colaborador en esa fecha? (AgroCloud#36)
 * Dos jornadas el mismo día para la misma persona es siempre un error de
 * captura, y pagaría el día dos veces.
 */
export function existsAttendanceDuplicate(
  records: AttendanceRecord[] = [],
  teamMemberId: string,
  date: string,
  excludeId?: string | null,
): boolean {
  return records.some(
    (r) =>
      r.teamMemberId === teamMemberId &&
      r.date === date &&
      (!excludeId || r.id !== excludeId),
  );
}

// ---------------------------------------------------------------------------
// MOVIMIENTOS — anticipos y otros haberes/descuentos (AgroCloud#37)
// ---------------------------------------------------------------------------

/**
 * Importe de una cuota de préstamo (AgroCloud#37). Un préstamo sin cuotas (o de 1) se
 * descuenta íntegro en la liquidación siguiente.
 */
export function computeInstallmentAmount(
  adjustment: Pick<PayrollAdjustment, "type" | "amount" | "installments">,
): number {
  const amount = round2(number(adjustment.amount));
  if (adjustment.type !== "loan") return amount;
  const installments = positive(adjustment.installments);
  if (installments === null || installments <= 1) return amount;
  return round2(amount / Math.trunc(installments));
}

/** Residuo por debajo del centavo: dinero que no existe en ninguna moneda. */
const CENT = 0.01;

/** Total de cuotas pactadas de un préstamo (AgroCloud#37); 1 si no se pactaron. */
function totalInstallments(
  adjustment: Pick<PayrollAdjustment, "installments">,
): number {
  const installments = positive(adjustment.installments);
  return installments === null || installments < 1 ? 1 : Math.trunc(installments);
}

/** Cuotas ya descontadas, saneadas (AgroCloud#37). */
function paidInstallmentsOf(
  adjustment: Pick<PayrollAdjustment, "paidInstallments">,
): number {
  return Math.max(0, Math.trunc(number(adjustment.paidInstallments)));
}

/**
 * Saldo pendiente de un préstamo (AgroCloud#37): lo prestado menos lo ya descontado.
 * Nunca negativo.
 *
 * Si ya se descontaron todas las cuotas pactadas el saldo es 0, aunque el
 * reparto no fuera exacto: 100 en 3 cuotas de 33,33 dejaba 1 centavo vivo que
 * generaba una imposible "cuota 4 de 3" (ver `computeApplicableAmount`, que
 * cobra el saldo completo en la última). Un residuo menor a un centavo tampoco
 * es deuda: no se puede cobrar.
 */
export function computeLoanBalance(
  adjustment: Pick<PayrollAdjustment, "type" | "amount" | "installments" | "paidInstallments">,
): number {
  const amount = round2(number(adjustment.amount));
  if (adjustment.type !== "loan") return amount;
  const paid = paidInstallmentsOf(adjustment);
  if (paid >= totalInstallments(adjustment)) return 0;
  const installment = computeInstallmentAmount(adjustment);
  const balance = round2(Math.max(0, amount - paid * installment));
  return balance < CENT ? 0 : balance;
}

/**
 * Lo que hay que descontar de ESTA liquidación por un movimiento (AgroCloud#37):
 * la cuota si es un préstamo vigente, el monto íntegro si no.
 * `0` si el préstamo ya está saldado (aquí el 0 SÍ es un hecho conocido).
 *
 * En la ÚLTIMA cuota se cobra el saldo completo, no la cuota teórica: 100 en 3
 * cuotas son 33,33 + 33,33 + 33,34, y no 33,33 tres veces con un centavo
 * colgando para siempre.
 */
export function computeApplicableAmount(adjustment: PayrollAdjustment): number {
  if (adjustment.type !== "loan") return round2(number(adjustment.amount));
  const balance = computeLoanBalance(adjustment);
  if (balance <= 0) return 0;
  const isLast = paidInstallmentsOf(adjustment) + 1 >= totalInstallments(adjustment);
  if (isLast) return balance;
  return round2(Math.min(balance, computeInstallmentAmount(adjustment)));
}

/**
 * Movimientos que una liquidación debe tomar (AgroCloud#37): del colaborador, en estado
 * `pending`, y del periodo (o sin periodo asignado = "el próximo que pase").
 * Los `applied` y `void` quedan fuera: descontar dos veces un anticipo es
 * robarle al colaborador.
 *
 * `payslipId` es el caso del RECÁLCULO: los movimientos que ESA MISMA
 * liquidación ya aplicó vuelven a entrar, porque si no el anticipo ya
 * descontado desaparecería de las líneas y el líquido subiría al recalcular.
 * Sus cuotas ya pagadas NO se vuelven a incrementar (ver `rebaseForPayslip`).
 */
export function filterPayableAdjustments(
  adjustments: PayrollAdjustment[] = [],
  teamMemberId: string,
  periodId?: string | null,
  payslipId?: string | null,
): PayrollAdjustment[] {
  return adjustments.filter((m) => {
    if (m.teamMemberId !== teamMemberId) return false;
    const status = m.status ?? "pending";
    const appliedByThisPayslip =
      Boolean(payslipId) && status === "applied" && m.payslipId === payslipId;
    if (status !== "pending" && !appliedByThisPayslip) return false;
    if (periodId && m.periodId && m.periodId !== periodId) return false;
    return true;
  });
}

/**
 * Devuelve el movimiento tal como estaba ANTES de que esta liquidación lo
 * aplicara (AgroCloud#37). Al aplicarse, un préstamo incrementó sus cuotas pagadas; si
 * el recálculo usara ese valor, la misma liquidación mostraría la cuota
 * siguiente y descontaría un tramo que nadie pagó todavía.
 */
export function rebaseForPayslip(
  adjustment: PayrollAdjustment,
  payslipId?: string | null,
): PayrollAdjustment {
  if (!payslipId || adjustment.payslipId !== payslipId) return adjustment;
  if ((adjustment.status ?? "pending") !== "applied") return adjustment;
  if (adjustment.type !== "loan") return adjustment;
  return {
    ...adjustment,
    paidInstallments: Math.max(0, paidInstallmentsOf(adjustment) - 1),
  };
}

/** Total de anticipos vigentes (pendientes) de un colaborador (AgroCloud#37). */
export function computeAdvancesPending(
  adjustments: PayrollAdjustment[] = [],
  teamMemberId?: string | null,
): number {
  return round2(
    adjustments
      .filter((m) => !teamMemberId || m.teamMemberId === teamMemberId)
      .filter((m) => (m.status ?? "pending") === "pending")
      .filter((m) => m.type === "advance" || m.type === "loan")
      .reduce((acc, m) => acc + computeLoanBalance(m), 0),
  );
}

// ---------------------------------------------------------------------------
// INCIDENCIAS — flujo de autorización campo → nómina (AgroCloud#81)
// ---------------------------------------------------------------------------

/** ¿La transición de estado está permitida por el flujo de autorización? (AgroCloud#81) */
export function canTransition(
  from: WorkLogStatus | undefined,
  hacia: WorkLogStatus,
): boolean {
  const origin = from ?? "submitted";
  const allowed = WORK_LOG_TRANSITIONS[origin];
  return Array.isArray(allowed) && allowed.includes(hacia);
}

/**
 * Incidencias que la nómina puede consumir (AgroCloud#81). Este filtro ES el criterio de
 * aceptación "solo lo autorizado llega a la nómina": todo lo `submitted` o
 * `rejected` se queda fuera, pase lo que pase.
 */
export function filterPayableWorkLogs(
  workLogs: WorkLog[] = [],
  teamMemberId?: string | null,
  periodId?: string | null,
): WorkLog[] {
  return workLogs.filter((i) => {
    if (!PAYABLE_WORK_LOG_STATUSES.includes(i.status ?? "submitted")) return false;
    if (teamMemberId && i.teamMemberId !== teamMemberId) return false;
    if (periodId && i.periodId && i.periodId !== periodId) return false;
    return true;
  });
}

/** Conteo del tablero de autorización del supervisor (AgroCloud#81). */
export function computeSummaryWorkLogs(
  workLogs: WorkLog[] = [],
): Record<WorkLogStatus, number> {
  const acc: Record<WorkLogStatus, number> = {
    submitted: 0,
    approved: 0,
    rejected: 0,
    sent_to_payroll: 0,
  };
  for (const i of workLogs) {
    const status = i.status ?? "submitted";
    if (status in acc) acc[status] += 1;
  }
  return acc;
}

// ---------------------------------------------------------------------------
// PREVISIONAL (AgroCloud#38)
// ---------------------------------------------------------------------------

/** Base imponible efectiva de un parámetro: el imponible, topado si hay tope. */
export function cappedTaxableBase(
  taxable: number,
  param: Pick<ContributionParam, "taxableCap">,
): number {
  const base = Math.max(0, round2(number(taxable)));
  const cap = positive(param.taxableCap);
  return cap === null ? base : round2(Math.min(base, cap));
}

/**
 * Descuentos previsionales del colaborador sobre la base imponible (AgroCloud#38). Solo
 * los parámetros activos con % > 0 generan línea.
 */
export function computeContributionDeductions(
  taxable: number,
  params: ContributionParam[] = [],
): CalculatedContributionDeduction[] {
  return params
    .filter((p) => p.active !== false)
    .map((p) => {
      const percentage = number(p.employeePercentage);
      const base = cappedTaxableBase(taxable, p);
      return {
        paramId: p.id,
        name: p.name,
        type: p.type,
        base,
        percentage,
        amount: round2((base * percentage) / 100),
      };
    })
    .filter((d) => d.percentage > 0);
}

/**
 * Aportes del empleador sobre la base imponible (AgroCloud#38). NO se descuentan del
 * líquido del colaborador: son costo empresa, y se informan aparte.
 */
export function computeEmployerContributions(
  taxable: number,
  params: ContributionParam[] = [],
): CalculatedContributionDeduction[] {
  return params
    .filter((p) => p.active !== false)
    .map((p) => {
      const percentage = number(p.employerPercentage);
      const base = cappedTaxableBase(taxable, p);
      return {
        paramId: p.id,
        name: p.name,
        type: p.type,
        base,
        percentage,
        amount: round2((base * percentage) / 100),
      };
    })
    .filter((d) => d.percentage > 0);
}

// ---------------------------------------------------------------------------
// LIQUIDACIÓN DE SUELDO (AgroCloud#38) — el corazón del módulo
// ---------------------------------------------------------------------------

export interface PayslipInput {
  teamMemberId: string;
  teamMemberName?: string | null;
  /** Periodo a liquidar; acota la asistencia y los movimientos considerados. */
  period?: { id?: string | null; startDate?: string | null; endDate?: string | null } | null;
  /**
   * Liquidación que se está RECALCULANDO. Los movimientos que ella misma ya
   * aplicó vuelven a entrar (con su cuota original), en vez de desaparecer de
   * las líneas y subir el líquido.
   */
  payslipId?: string | null;
  /**
   * Cómo se calcula el sueldo base del colaborador (#530): `daily` (días de
   * asistencia × tarifa diaria), `hourly` (horas de asistencia × tarifa por
   * hora) o `fixed_salary` (sueldo del periodo). Por defecto `daily`.
   */
  payMode?: "hourly" | "daily" | "fixed_salary" | null;
  /** Tarifa diaria del colaborador (maestro Equipo, AgroCloud#11). */
  dailyRate?: number | null;
  /** Tarifa por hora del colaborador (#530). */
  hourlyRate?: number | null;
  /** Sueldo fijo por periodo del colaborador (#530). */
  fixedSalary?: number | null;
  /** Asistencia del periodo (AgroCloud#36). */
  attendance?: AttendanceRecord[];
  /** Incidencias del equipo; SOLO las autorizadas se usan (AgroCloud#81). */
  workLogs?: WorkLog[];
  /** Anticipos y otros haberes/descuentos (AgroCloud#37). */
  adjustments?: PayrollAdjustment[];
  /** Parámetros previsionales vigentes (AgroCloud#38). */
  contributionParams?: ContributionParam[];
  /** Tarifas de pago de `@torre/team` (AgroCloud#13), indexadas por id. */
  rates?: Record<string, PayRateLike>;
  /** Haberes/descuentos capturados a mano sobre la liquidación. */
  manualLines?: Omit<PayslipLine, "origin" | "status">[];
}

export interface PayslipDeps {
  /**
   * `computeSettlementPayment` de `@torre/team` (AgroCloud#13). Si no se inyecta,
   * toda línea que dependa de la fórmula de pago queda en `null` con
   * `team_module_missing` — la liquidación NO se puede pagar.
   */
  computePayment?: ComputePaymentFn | null;
}

/** El motivo más grave manda: sin módulo > sin tarifa > sin cálculo > sin día > ok. */
const STATUS_SEVERITY: Record<LineStatus, number> = {
  ok: 0,
  no_daily_rate: 1,
  no_hourly_rate: 1,
  no_salary: 1,
  // Derivado de otra línea: nunca debe tapar al motivo de verdad.
  no_taxable_base: 1,
  no_payment_calc: 2,
  no_rate: 3,
  team_module_missing: 4,
};

function worstStatus(a: LineStatus, b: LineStatus): LineStatus {
  return STATUS_SEVERITY[b] > STATUS_SEVERITY[a] ? b : a;
}

/**
 * Traduce el resultado de `computeSettlementPayment` de Colaboradores (AgroCloud#13/#100) a
 * `{ amount, status, noCalcReason }`.
 *
 * ⚠️ CONTRATO AgroCloud#100: `total: null` + `noCalcReason` cuando no se puede
 * calcular. Ese `null` NO se convierte en 0: `number(null)` daría 0 y pagaría de
 * menos. Sólo un número finito real se acepta como importe; en otro caso la
 * línea queda sin monto, `status: "no_payment_calc"` y el motivo sube a la UI.
 */
function amountFromPayment(payment: PaymentCalculationLike): {
  amount: number | null;
  status: LineStatus;
  noCalcReason: string | null;
} {
  const total = payment?.total;
  if (total === null || total === undefined || !Number.isFinite(Number(total))) {
    return {
      amount: null,
      status: "no_payment_calc",
      noCalcReason: payment?.noCalcReason ?? "no_payment_calc",
    };
  }
  return { amount: round2(number(total)), status: "ok", noCalcReason: null };
}

/**
 * Línea del sueldo base por las jornadas trabajadas (AgroCloud#36 → AgroCloud#38).
 * DELEGA el cálculo en `@torre/team` (modalidad día).
 */
function baseSalaryLine(
  input: Pick<PayslipInput, "payMode" | "dailyRate" | "hourlyRate" | "fixedSalary">,
  days: number,
  hours: number,
  computePayment: ComputePaymentFn | null | undefined,
): PayslipLine | null {
  const mode = input.payMode ?? "daily";
  const base: PayslipLine = {
    id: "base-salary",
    type: "earning",
    concept: "Sueldo por jornadas trabajadas",
    amount: null,
    taxable: true,
    origin: "attendance",
    referenceId: null,
    status: "ok",
  };

  if (mode === "fixed_salary") {
    // El sueldo fijo se paga por periodo, sin depender de la asistencia.
    const salaryLine: PayslipLine = { ...base, concept: "Sueldo fijo del periodo" };
    const salary = positive(input.fixedSalary);
    if (salary === null) return { ...salaryLine, status: "no_salary" };
    if (typeof computePayment !== "function") return { ...salaryLine, status: "team_module_missing" };
    const payment = computePayment({ mode: "fixed_salary", fixedSalary: salary }, { periods: 1 });
    return { ...salaryLine, ...amountFromPayment(payment) };
  }

  if (mode === "hourly") {
    if (hours <= 0) return null;
    const hourlyLine: PayslipLine = { ...base, concept: "Sueldo por horas trabajadas" };
    const rate = positive(input.hourlyRate);
    if (rate === null) return { ...hourlyLine, status: "no_hourly_rate" };
    if (typeof computePayment !== "function") return { ...hourlyLine, status: "team_module_missing" };
    const payment = computePayment({ mode: "hourly", hourlyRate: rate }, { hours });
    return { ...hourlyLine, ...amountFromPayment(payment) };
  }

  if (days <= 0) return null;
  const dailyRate = input.dailyRate;
  const daily = positive(dailyRate);
  if (daily === null) return { ...base, status: "no_daily_rate" };
  if (typeof computePayment !== "function")
    return { ...base, status: "team_module_missing" };

  const payment = computePayment({ mode: "daily", dailyRate: daily }, { days });
  // `null` de AgroCloud#100 no se lava a 0: la línea queda sin monto y la liquidación
  // entera se marcará incompleta.
  return { ...base, ...amountFromPayment(payment) };
}

/** Línea generada por una incidencia autorizada (AgroCloud#81 → AgroCloud#38). */
function lineFromWorkLog(
  workLog: WorkLog,
  rates: Record<string, PayRateLike>,
  computePayment: ComputePaymentFn | null | undefined,
): PayslipLine {
  const isDeduction = workLog.type === "deduction";
  const line: PayslipLine = {
    id: `work_log-${workLog.id}`,
    type: isDeduction ? "deduction" : "earning",
    concept: workLog.concept || `Incidencia ${workLog.type}`,
    amount: null,
    taxable: !isDeduction,
    origin: "work_log",
    referenceId: workLog.id,
    status: "ok",
  };

  // 1) El supervisor autorizó un monto explícito → manda. No hay nada que
  //    recalcular: autorizó ESA cifra (criterio AgroCloud#81).
  if (workLog.amount !== null && workLog.amount !== undefined) {
    return { ...line, amount: round2(Math.abs(number(workLog.amount))) };
  }

  // 2) Entregable con tarifa → se DELEGA en @torre/team (AgroCloud#13).
  if (workLog.payRateId) {
    const rate = rates[workLog.payRateId];
    if (!rate) return { ...line, status: "no_rate" };
    if (typeof computePayment !== "function")
      return { ...line, status: "team_module_missing" };
    // Se le pasan TODAS las cantidades que la incidencia capturó: una tarifa por
    // hora sin `hours` (o una por proyecto sin avance) devolvía "sin horas" y
    // dejaba la liquidación entera impagable con el dato delante.
    const payment = computePayment(rate, {
      output: workLog.output ?? null,
      days: workLog.days ?? null,
      hours: workLog.hours ?? null,
      progressPercentage: workLog.progressPercentage ?? null,
      previouslyPaid: workLog.previouslyPaid ?? null,
      periods: workLog.periods ?? null,
    });
    // Contrato AgroCloud#100: `total: null` + `noCalcReason` si la tarifa no cubre
    // esos entregables (o falta el dato). NO se convierte en 0.
    return {
      ...line,
      concept: workLog.concept || workLog.payRateName || "Trabajo por entregable",
      ...amountFromPayment(payment),
    };
  }

  // 3) Ni monto ni tarifa: no hay forma honesta de saber cuánto vale.
  return { ...line, status: "no_rate" };
}

/**
 * Por qué la base imponible quedó sin calcular (AgroCloud#38): el peor motivo de los
 * haberes que quedaron sin monto. Si ninguno lo dice (p. ej. un haber manual
 * sin importe), se usa el motivo propio `no_taxable_base`.
 */
function causeOfUnknownTaxableBase(lines: PayslipLine[]): LineStatus {
  const cause = lines
    .filter((l) => l.type === "earning" && l.amount === null)
    .reduce<LineStatus>((acc, l) => worstStatus(acc, l.status ?? "ok"), "ok");
  return cause === "ok" ? "no_taxable_base" : cause;
}

/** Línea generada por un anticipo/descuento/haber (AgroCloud#37 → AgroCloud#38). */
function lineFromAdjustment(adjustment: PayrollAdjustment): PayslipLine {
  const sign = ADJUSTMENT_SIGN[adjustment.type] ?? -1;
  const amount = computeApplicableAmount(adjustment);
  const installments = positive(adjustment.installments);
  // La glosa nunca puede decir "cuota 4 de 3": el número de cuota se acota al
  // total pactado.
  const total = installments === null ? 0 : Math.trunc(installments);
  const suffix =
    adjustment.type === "loan" && total > 1
      ? ` (cuota ${Math.min(paidInstallmentsOf(adjustment) + 1, total)}/${total})`
      : "";

  return {
    id: `adjustment-${adjustment.id}`,
    type: sign < 0 ? "deduction" : "earning",
    concept: `${adjustment.concept}${suffix}`,
    amount: round2(Math.abs(amount)),
    taxable: sign > 0 ? adjustment.taxable !== false : undefined,
    origin: "adjustment",
    referenceId: adjustment.id,
    status: "ok",
  };
}

/**
 * Calcula una liquidación de sueldo completa (AgroCloud#38): toma la asistencia (AgroCloud#36),
 * las incidencias AUTORIZADAS (AgroCloud#81) y los anticipos/haberes/descuentos (AgroCloud#37),
 * aplica lo previsional y devuelve el líquido a pagar.
 *
 * El pago de la tarea se DELEGA en `deps.computePayment`
 * (`computeSettlementPayment` de `@torre/team`, AgroCloud#13). Sin esa función,
 * las líneas que dependen de ella quedan en `null` y la liquidación se marca
 * `complete: false` con `netPay: null`: preferimos no pagar a pagar mal.
 */
export function computePayslip(
  input: PayslipInput,
  deps: PayslipDeps = {},
): PayslipCalculation {
  const { computePayment } = deps;
  const rates = input.rates ?? {};
  const from = input.period?.startDate ?? null;
  const to = input.period?.endDate ?? null;
  const periodId = input.period?.id ?? null;

  const lines: PayslipLine[] = [];

  // --- 1) Sueldo por asistencia (AgroCloud#36) --------------------------------------
  const daysWorked = computePeriodDays(
    input.attendance ?? [],
    input.teamMemberId,
    from,
    to,
  );
  const hoursWorked = computePeriodHours(input.attendance ?? [], input.teamMemberId, from, to);
  const salary = baseSalaryLine(input, daysWorked, hoursWorked, computePayment);
  if (salary) lines.push(salary);

  // --- 2) Incidencias AUTORIZADAS (AgroCloud#81) ------------------------------------
  // El filtro es el criterio de aceptación: lo capturado sin autorizar y lo
  // rechazado NO entra aquí jamás.
  const workLogs = filterPayableWorkLogs(
    input.workLogs ?? [],
    input.teamMemberId,
    periodId,
  ).filter((i) => !(from || to) || inTier(i.date, from, to));
  for (const workLog of workLogs) {
    lines.push(lineFromWorkLog(workLog, rates, computePayment));
  }

  // --- 3) Haberes y descuentos manuales ------------------------------------
  for (const manual of input.manualLines ?? []) {
    lines.push({ ...manual, origin: "manual", status: "ok" });
  }

  // --- 4) Anticipos y otros movimientos (AgroCloud#37) ------------------------------
  const adjustments = filterPayableAdjustments(
    input.adjustments ?? [],
    input.teamMemberId,
    periodId,
    input.payslipId ?? null,
  );
  for (const adjustment of adjustments) {
    lines.push(lineFromAdjustment(rebaseForPayslip(adjustment, input.payslipId ?? null)));
  }

  // --- 5) Base imponible ---------------------------------------------------
  // Si un haber imponible es desconocido, la base imponible es desconocida y
  // TODO lo previsional también: no se calcula un % sobre una base inventada.
  const earnings = lines.filter((l) => l.type === "earning");
  const taxableTotal = strictSum(
    earnings.filter((l) => l.taxable !== false).map((l) => l.amount),
  );

  // --- 6) Previsional (AgroCloud#38) ------------------------------------------------
  const params = input.contributionParams ?? [];
  let totalContributions = 0;
  let totalEmployerContribution = 0;

  if (taxableTotal !== null) {
    for (const d of computeContributionDeductions(taxableTotal, params)) {
      lines.push({
        id: `contribution-${d.paramId}`,
        type: "deduction",
        concept: `${d.name} (${d.percentage}%)`,
        amount: d.amount,
        origin: "contribution",
        referenceId: d.paramId,
        status: "ok",
      });
      totalContributions = round2(totalContributions + d.amount);
    }
    totalEmployerContribution = round2(
      computeEmployerContributions(taxableTotal, params).reduce(
        (acc, a) => acc + a.amount,
        0,
      ),
    );
  } else if (params.some((p) => p.active !== false && number(p.employeePercentage) > 0)) {
    // Hay previsional que aplicar pero no se sabe sobre qué base: la línea
    // existe y es desconocida, para que el total quede honestamente en null.
    //
    // Su motivo se HEREDA del haber que dejó la base sin calcular (si falta la
    // tarifa dirá "sin tarifa", no "falta el módulo Equipo"): esta línea es una
    // consecuencia, y mandar al usuario a revisar el módulo equivocado le hace
    // perder el tiempo donde no está el problema.
    lines.push({
      id: "contribution-undetermined",
      type: "deduction",
      concept: "Descuentos previsionales (base imponible indeterminada)",
      amount: null,
      origin: "contribution",
      referenceId: null,
      status: causeOfUnknownTaxableBase(lines),
    });
  }

  // --- 7) Totales ----------------------------------------------------------
  const deductions = lines.filter((l) => l.type === "deduction");
  const totalEarnings = strictSum(earnings.map((l) => l.amount));
  const totalDeductions = strictSum(deductions.map((l) => l.amount));

  const complete = lines.every((l) => l.amount !== null);
  const paymentStatus = lines.reduce<LineStatus>(
    (acc, l) => worstStatus(acc, l.status ?? "ok"),
    "ok",
  );

  const netPay =
    complete && totalEarnings !== null && totalDeductions !== null
      ? round2(totalEarnings - totalDeductions)
      : null;

  return {
    lines,
    daysWorked,
    totalEarnings,
    taxableTotal,
    totalDeductions,
    totalContributions,
    totalEmployerContribution,
    netPay,
    complete,
    paymentStatus,
  };
}

// ---------------------------------------------------------------------------
// PERIODOS (AgroCloud#82)
// ---------------------------------------------------------------------------

/** Días naturales pagados en un periodo (AgroCloud#82), ambos extremos inclusive. */
export function computeDaysPaid(
  startDate?: string | null,
  endDate?: string | null,
): number | null {
  if (!startDate || !endDate) return null;
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.round((end - start) / 86400000) + 1;
}

/**
 * ¿Se puede cerrar el periodo? (AgroCloud#82) Un periodo se cierra solo si TODAS sus
 * liquidaciones están completas y aprobadas/pagadas. Cerrar un periodo con una
 * liquidación incompleta es congelar un sueldo mal pagado.
 */
export function canClosePeriod(
  period: Pick<PayrollPeriod, "status">,
  payslips: Payslip[] = [],
): ValidationResult {
  const errors: string[] = [];
  if ((period.status ?? "open") === "closed") {
    errors.push("El periodo ya está cerrado");
  }
  if (payslips.length === 0) {
    errors.push("El periodo no tiene liquidaciones que cerrar");
  }

  const active = payslips.filter((l) => l.status !== "void");
  const incomplete = active.filter((l) => l.calculation && l.calculation.complete === false);
  if (incomplete.length > 0) {
    errors.push(
      `Hay ${incomplete.length} liquidación(es) incompleta(s): no se puede cerrar el periodo`,
    );
  }
  const noApproved = active.filter(
    (l) => l.status !== "approved" && l.status !== "paid",
  );
  if (noApproved.length > 0) {
    errors.push(`Hay ${noApproved.length} liquidación(es) sin aprobar`);
  }
  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// CFDI DE NÓMINA (AgroCloud#82) — contrato + PUERTO de timbrado
// ---------------------------------------------------------------------------
//
// ⚠️ LEER ANTES DE TOCAR ESTO. Timbrar un CFDI de nómina ante el SAT requiere:
//   • un PAC (Proveedor Autorizado de Certificación) contratado,
//   • un Certificado de Sello Digital (CSD) del emisor con su llave privada,
//   • el XML sellado según el Anexo 20 + complemento Nómina 1.2.
// Nada de eso existe en este repo. Este módulo, por tanto:
//   1. ARMA el comprobante (`buildPayrollReceipt`) con los datos que el PAC
//      necesitaría, y lo VALIDA (`validatePayrollReceipt`).
//   2. Define el PUERTO (`StampPort`) que un adaptador de PAC real
//      implementaría el día que se contrate uno.
//   3. Si no hay PAC, devuelve `status: "no_pac_configured"` y NO inventa
//      UUID, sello, cadena original ni XML timbrado.
// Un CFDI fabricado no es un stub: es un documento fiscal falso.
// ---------------------------------------------------------------------------

export interface IssuerCfdi {
  rfc: string;
  name: string;
  /** Clave del régimen fiscal (catálogo SAT `c_tax_regime`). */
  taxRegime?: string | null;
  /** Registro patronal ante el IMSS. */
  recordEmployer?: string | null;
  codePostal?: string | null;
}

export interface RecipientCfdi {
  rfc: string;
  name: string;
  curp?: string | null;
  numSecuritySocial?: string | null;
  startDateRelLabor?: string | null;
  /** Antigüedad en semanas (formato SAT `PnW`). */
  seniority?: string | null;
  typeContract?: string | null;
  typeRegime?: string | null;
  numEmployee?: string | null;
  payFrequency?: PayFrequency;
  wageBaseCotApor?: number | null;
  wageDailyIntegrated?: number | null;
}

/** Percepción o deducción del complemento de nómina (AgroCloud#82). */
export interface ConceptCfdi {
  /** Clave del catálogo SAT (`c_type_earning` / `c_type_deduction`). */
  code: string;
  concept: string;
  amountTaxed: number;
  amountExempt: number;
}

export interface PayrollReceipt {
  version: "4.0";
  /** Versión del complemento de nómina. */
  versionPayroll: "1.2";
  payrollType: PayrollType;
  payDate: string;
  dateInitialPayment: string;
  dateFinalPayment: string;
  numDaysPaid: number;
  issuer: IssuerCfdi;
  recipient: RecipientCfdi;
  earnings: ConceptCfdi[];
  deductions: ConceptCfdi[];
  totalEarnings: number;
  totalDeductions: number;
  /** Neto = percepciones − deducciones. */
  total: number;
  currency: string;
  /** Liquidación de la que salió (trazabilidad interna, no va al SAT). */
  payslipId?: string | null;
}

export interface ReceiptInput {
  payslip: Payslip;
  period: PayrollPeriod;
  issuer: IssuerCfdi;
  recipient: RecipientCfdi;
  /** Claves SAT por concepto; sin mapeo se usa "001"/"002" genéricos. */
  earningCodes?: Record<string, string>;
  deductionCodes?: Record<string, string>;
}

/**
 * Arma el comprobante de nómina a partir de la liquidación (AgroCloud#82).
 *
 * Devuelve `null` si la liquidación está **incompleta**: no se puede emitir un
 * documento fiscal sobre importes que no se conocen.
 */
export function buildPayrollReceipt(input: ReceiptInput): PayrollReceipt | null {
  const calculation = input.payslip.calculation;
  if (!calculation || !calculation.complete || calculation.netPay === null) return null;

  const days = computeDaysPaid(input.period.startDate, input.period.endDate);
  if (days === null) return null;

  const earnings: ConceptCfdi[] = calculation.lines
    .filter((l) => l.type === "earning" && l.amount !== null)
    .map((l) => ({
      code: input.earningCodes?.[l.id] ?? "001",
      concept: l.concept,
      // Gravado/exento depende de la naturaleza fiscal del concepto; sin el
      // mapeo del contador todo se declara gravado (el criterio conservador).
      amountTaxed: round2(number(l.amount)),
      amountExempt: 0,
    }));

  const deductions: ConceptCfdi[] = calculation.lines
    .filter((l) => l.type === "deduction" && l.amount !== null)
    .map((l) => ({
      code: input.deductionCodes?.[l.id] ?? "002",
      concept: l.concept,
      amountTaxed: round2(number(l.amount)),
      amountExempt: 0,
    }));

  return {
    version: "4.0",
    versionPayroll: "1.2",
    payrollType: input.period.payrollType ?? "regular",
    payDate: input.period.payDate ?? input.period.endDate,
    dateInitialPayment: input.period.startDate,
    dateFinalPayment: input.period.endDate,
    numDaysPaid: days,
    issuer: input.issuer,
    recipient: {
      ...input.recipient,
      payFrequency: input.recipient.payFrequency ?? input.period.frequency,
    },
    earnings,
    deductions,
    totalEarnings: round2(number(calculation.totalEarnings)),
    totalDeductions: round2(number(calculation.totalDeductions)),
    total: round2(calculation.netPay),
    currency: input.payslip.currency ?? input.period.currency ?? "MXN",
    payslipId: input.payslip.id,
  };
}

/** RFC mexicano: 3 letras (moral) o 4 (física) + 6 dígitos de fecha + homoclave. */
export const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

/** ¿El RFC tiene forma válida? (AgroCloud#82) No verifica que exista ante el SAT. */
export function isValidRfc(rfc?: string | null): boolean {
  return RFC_REGEX.test(String(rfc ?? "").trim().toUpperCase());
}

/**
 * Valida el comprobante ANTES de mandarlo a timbrar (AgroCloud#82). El PAC rechaza y
 * cobra igual: es más barato (y más rápido) validar aquí.
 */
export function validatePayrollReceipt(
  receipt: PayrollReceipt | null | undefined,
): ValidationResult {
  const errors: string[] = [];
  if (!receipt) {
    return {
      valid: false,
      errors: ["No hay comprobante que timbrar (la liquidación está incompleta)"],
    };
  }
  if (!isValidRfc(receipt.issuer?.rfc)) errors.push("El RFC del emisor no es válido");
  if (!isNonEmptyText(receipt.issuer?.name))
    errors.push("El nombre/razón social del emisor es obligatorio");
  if (!isNonEmptyText(receipt.issuer?.taxRegime))
    errors.push("El régimen fiscal del emisor es obligatorio");
  if (!isValidRfc(receipt.recipient?.rfc))
    errors.push("El RFC del receptor (colaborador) no es válido");
  if (!isNonEmptyText(receipt.recipient?.name))
    errors.push("El nombre del receptor (colaborador) es obligatorio");
  if (!isNonEmptyText(receipt.recipient?.curp))
    errors.push("La CURP del receptor es obligatoria en el complemento de nómina");
  if (!isNonEmptyText(receipt.payDate)) errors.push("La fecha de pago es obligatoria");
  if (!(receipt.numDaysPaid > 0))
    errors.push("Los días pagados deben ser mayores a 0");
  if (!receipt.earnings || receipt.earnings.length === 0)
    errors.push("El comprobante debe tener al menos una percepción");

  const sumEarnings = round2(
    (receipt.earnings ?? []).reduce(
      (acc, p) => acc + number(p.amountTaxed) + number(p.amountExempt),
      0,
    ),
  );
  if (sumEarnings !== round2(number(receipt.totalEarnings))) {
    errors.push("El total de percepciones no cuadra con la suma de sus conceptos");
  }
  const sumDeductions = round2(
    (receipt.deductions ?? []).reduce(
      (acc, d) => acc + number(d.amountTaxed) + number(d.amountExempt),
      0,
    ),
  );
  if (sumDeductions !== round2(number(receipt.totalDeductions))) {
    errors.push("El total de deducciones no cuadra con la suma de sus conceptos");
  }
  if (round2(sumEarnings - sumDeductions) !== round2(number(receipt.total))) {
    errors.push("El neto no cuadra con percepciones − deducciones");
  }
  return { valid: errors.length === 0, errors };
}

/** Resultado del intento de timbrado (AgroCloud#82). */
export interface StampResult {
  status: StampStatus;
  /** Folio fiscal que asigna el SAT. `null` mientras no haya timbrado real. */
  uuid?: string | null;
  dateStamped?: string | null;
  sealSat?: string | null;
  sealCfd?: string | null;
  noCertificateSat?: string | null;
  chainOriginal?: string | null;
  /** XML timbrado devuelto por el PAC. */
  xml?: string | null;
  /** Nombre del PAC que timbró. */
  pac?: string | null;
  /** Por qué no se timbró. */
  error?: string | null;
}

/**
 * **Puerto de timbrado** (AgroCloud#82): lo que implementaría un adaptador de PAC real
 * (Facturama, Finkok, SW Sapien…). Recibe el comprobante armado y devuelve el
 * resultado del SAT. Este repo NO trae ninguna implementación.
 */
export type StampPort = (receipt: PayrollReceipt) => Promise<StampResult>;

/** Mensaje único de "no hay PAC" — la UI y el back dicen lo mismo (AgroCloud#82). */
export const NO_PAC_MESSAGE =
  "No hay un PAC (Proveedor Autorizado de Certificación) configurado. " +
  "El timbrado ante el SAT requiere contratar un PAC y cargar el Certificado de " +
  "Sello Digital del emisor. El comprobante queda armado y validado, listo para " +
  "timbrarse en cuanto se configure.";

/**
 * Intenta timbrar el comprobante (AgroCloud#82). Orden: validar → delegar en el PAC.
 *
 * Sin PAC inyectado devuelve `no_pac_configured` con `uuid: null`. **Jamás
 * fabrica un folio fiscal**: un CFDI inventado es un documento falso, y este
 * código lo sabe.
 */
export async function stampReceipt(
  receipt: PayrollReceipt | null | undefined,
  pac?: StampPort | null,
): Promise<StampResult> {
  const validation = validatePayrollReceipt(receipt);
  if (!validation.valid) {
    return {
      status: "error",
      uuid: null,
      error: validation.errors.join("; "),
    };
  }
  if (typeof pac !== "function") {
    return { status: "no_pac_configured", uuid: null, error: NO_PAC_MESSAGE };
  }
  try {
    return await pac(receipt as PayrollReceipt);
  } catch (err) {
    return {
      status: "error",
      uuid: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** ¿La liquidación tiene un comprobante fiscal válido y timbrado? (AgroCloud#82) */
export function isStamped(payslip: Pick<Payslip, "stamped">): boolean {
  return (
    payslip.stamped?.status === "stamped" && isNonEmptyText(payslip.stamped?.uuid)
  );
}

// ---------------------------------------------------------------------------
// MARCAJE / reloj checador (AgroCloud#83) — contrato declarado, SIN implementación
// ---------------------------------------------------------------------------
//
// ⚠️ La issue AgroCloud#83 está etiquetada `alcance-a-evaluar` y requiere hardware
// (lector facial/huella, tags NFC o un reloj checador). Aquí se declara SOLO el
// modelo de datos de un marcaje, para que el día que se apruebe el hardware
// exista el contrato de ingesta. NO hay integración, NO hay biometría, y la
// sección del front lo dice: es una maqueta `coming-soon`.
// ---------------------------------------------------------------------------

export interface TimeClockPunch {
  id: string;
  teamMemberId: string;
  teamMemberName?: string | null;
  /** Instante del marcaje (ISO 8601). */
  timestamp: string;
  type: "clockIn" | "clockOut";
  source: ClockSource;
  /** Identificador del dispositivo/reloj que lo emitió. */
  deviceId?: string | null;
  locationId?: string | null;
  /** Confianza del match biométrico 0–100, si la fuente la reporta. */
  trust?: number | null;
}

/** Estado real de la integración de presencia biométrica (AgroCloud#83). */
export const TIME_CLOCK_STATUS = "coming-soon" as const;

// ---------------------------------------------------------------------------
// VALIDADORES (puros, compartidos front/back)
// ---------------------------------------------------------------------------

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const fail = (errors: string[]): ValidationResult => ({
  valid: errors.length === 0,
  errors,
});

/** Valida un registro de asistencia (AgroCloud#36). */
export function validateAttendance(input: Partial<AttendanceRecord>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.date)) errors.push("La fecha es obligatoria");
  if (!isNonEmptyText(input.teamMemberId)) errors.push("El colaborador es obligatorio");
  if (input.status != null && !ATTENDANCE_STATUSES.includes(input.status)) {
    errors.push("El estado de asistencia no es válido");
  }
  if (input.checkIn != null && input.checkIn !== "" && hourToMinutes(input.checkIn) === null) {
    errors.push("La hora de entrada debe tener formato HH:MM");
  }
  if (input.checkOut != null && input.checkOut !== "" && hourToMinutes(input.checkOut) === null) {
    errors.push("La hora de salida debe tener formato HH:MM");
  }
  if (input.days != null) {
    const j = Number(input.days);
    if (!Number.isFinite(j) || j < 0 || j > 2) {
      errors.push("Las jornadas deben estar entre 0 y 2");
    }
  }
  if (input.overtime != null && number(input.overtime) < 0) {
    errors.push("Las horas extra no pueden ser negativas");
  }
  if (input.source != null && !CLOCK_SOURCES.includes(input.source)) {
    errors.push("La fuente de marcaje no es válida");
  }
  return fail(errors);
}

/** Valida un anticipo u otro haber/descuento (AgroCloud#37). */
export function validateAdjustment(input: Partial<PayrollAdjustment>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.date)) errors.push("La fecha es obligatoria");
  if (!isNonEmptyText(input.teamMemberId)) errors.push("El colaborador es obligatorio");
  if (!isNonEmptyText(input.concept)) errors.push("El concepto es obligatorio");
  if (input.type == null || !ADJUSTMENT_TYPES.includes(input.type)) {
    errors.push("El tipo de movimiento no es válido");
  }
  // Un movimiento de 0 no descuenta ni paga nada: es basura en la liquidación.
  if (positive(input.amount) === null) errors.push("El monto debe ser mayor a 0");
  if (input.installments != null) {
    const c = Number(input.installments);
    if (!Number.isFinite(c) || c < 1 || !Number.isInteger(c)) {
      errors.push("Las cuotas deben ser un entero mayor o igual a 1");
    }
  }
  if (input.paidInstallments != null) {
    const p = Number(input.paidInstallments);
    const c = Number(input.installments ?? 1);
    if (!Number.isFinite(p) || p < 0) errors.push("Las cuotas pagadas no pueden ser negativas");
    else if (Number.isFinite(c) && p > c) errors.push("Las cuotas pagadas no pueden superar el total de cuotas");
  }
  if (input.status != null && !ADJUSTMENT_STATUSES.includes(input.status)) {
    errors.push("El estado del movimiento no es válido");
  }
  return fail(errors);
}

/** Valida una incidencia del equipo (AgroCloud#81). */
export function validateWorkLog(input: Partial<WorkLog>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.date)) errors.push("La fecha es obligatoria");
  if (!isNonEmptyText(input.teamMemberId)) errors.push("El colaborador es obligatorio");
  if (input.type == null || !WORK_LOG_TYPES.includes(input.type)) {
    errors.push("El tipo de incidencia no es válido");
  }
  if (input.status != null && !WORK_LOG_STATUSES.includes(input.status)) {
    errors.push("El estado de la incidencia no es válido");
  }
  if (input.amount != null && number(input.amount) < 0) {
    errors.push("El monto no puede ser negativo");
  }
  // Una incidencia que no dice ni cuánto vale ni con qué tarifa calcularlo no
  // se puede autorizar: el supervisor estaría firmando en blanco (AgroCloud#81).
  if (
    input.amount == null &&
    !isNonEmptyText(input.payRateId) &&
    input.type !== "attendance"
  ) {
    errors.push("La incidencia necesita un monto o una tarifa de pago para valorizarse");
  }
  if (input.origin != null && !WORK_LOG_ORIGINS.includes(input.origin)) {
    errors.push("El origen no es válido");
  }
  return fail(errors);
}

/**
 * Valida un haber/descuento capturado a mano sobre la liquidación (AgroCloud#38).
 *
 * Los nombres son los del contrato real (`type`/`concept`/`amount`): validar
 * otros campos deja pasar sin revisar los que sí llegan, y por ahí entra un
 * haber de cualquier monto o un importe que ni siquiera es un número.
 */
export function validateManualLine(
  input: Partial<PayslipLine> | null | undefined,
): ValidationResult {
  const errors: string[] = [];
  if (!input || typeof input !== "object") {
    return fail(["La línea manual no es válida"]);
  }
  if (input.type !== "earning" && input.type !== "deduction") {
    errors.push('El tipo de la línea manual debe ser "earning" o "deduction"');
  }
  if (!isNonEmptyText(input.concept)) {
    errors.push("El concepto de la línea manual es obligatorio");
  }
  // Un importe que no es número (o que no se conoce) no se puede pagar, y un 0
  // no descuenta ni paga nada: es basura en la liquidación.
  if (typeof input.amount !== "number" || !Number.isFinite(input.amount)) {
    errors.push("El importe de la línea manual debe ser un número");
  } else if (input.amount <= 0) {
    errors.push("El importe de la línea manual debe ser mayor a 0");
  }
  return fail(errors);
}

/** Valida todas las líneas manuales de una liquidación (AgroCloud#38). */
export function validateManualLines(
  lines: unknown,
): ValidationResult {
  if (lines === undefined || lines === null) return fail([]);
  if (!Array.isArray(lines)) return fail(["Las líneas manuales deben ser una lista"]);
  const errors: string[] = [];
  lines.forEach((line, i) => {
    for (const e of validateManualLine(line as Partial<PayslipLine>).errors) {
      errors.push(`Línea manual ${i + 1}: ${e}`);
    }
  });
  return fail(errors);
}

/** Valida una transición del flujo de autorización (AgroCloud#81). */
export function validateTransitionWorkLog(
  workLog: Pick<WorkLog, "status">,
  hacia: WorkLogStatus,
  rejectionReason?: string | null,
): ValidationResult {
  const errors: string[] = [];
  const from = workLog.status ?? "submitted";
  if (!WORK_LOG_STATUSES.includes(hacia)) {
    errors.push("El estado destino no es válido");
    return fail(errors);
  }
  if (!canTransition(from, hacia)) {
    errors.push(`No se puede pasar de "${from}" a "${hacia}"`);
  }
  // Rechazar sin decir por qué deja al colaborador sin saber qué corregir.
  if (hacia === "rejected" && !isNonEmptyText(rejectionReason)) {
    errors.push("Rechazar una incidencia requiere indicar el motivo");
  }
  return fail(errors);
}

/** Valida un parámetro previsional (AgroCloud#38). */
export function validateContributionParam(
  input: Partial<ContributionParam>,
): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.name)) errors.push("El nombre es obligatorio");
  if (input.type == null || !CONTRIBUTION_TYPES.includes(input.type)) {
    errors.push("El tipo previsional no es válido");
  }
  for (const field of ["employeePercentage", "employerPercentage"] as const) {
    const v = input[field];
    if (v != null) {
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        errors.push(`${field} debe estar entre 0 y 100`);
      }
    }
  }
  if (input.taxableCap != null && number(input.taxableCap) < 0) {
    errors.push("El tope imponible no puede ser negativo");
  }
  return fail(errors);
}

/** Valida un periodo de nómina (AgroCloud#82). */
export function validatePeriod(input: Partial<PayrollPeriod>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.name)) errors.push("El nombre del periodo es obligatorio");
  if (!isNonEmptyText(input.startDate)) errors.push("La fecha de inicio es obligatoria");
  if (!isNonEmptyText(input.endDate)) errors.push("La fecha de término es obligatoria");
  if (
    isNonEmptyText(input.startDate) &&
    isNonEmptyText(input.endDate) &&
    String(input.endDate) < String(input.startDate)
  ) {
    errors.push("La fecha de término no puede ser anterior a la de inicio");
  }
  if (input.frequency != null && !PAY_FREQUENCIES.includes(input.frequency)) {
    errors.push("La periodicidad no es válida");
  }
  if (input.payrollType != null && !PAYROLL_TYPES.includes(input.payrollType)) {
    errors.push("El tipo de nómina no es válido");
  }
  if (input.status != null && !PERIOD_STATUSES.includes(input.status)) {
    errors.push("El estado del periodo no es válido");
  }
  return fail(errors);
}

/** Valida una liquidación de sueldo (AgroCloud#38). */
export function validatePayslip(input: Partial<Payslip>): ValidationResult {
  const errors: string[] = [];
  if (!isNonEmptyText(input.teamMemberId)) errors.push("El colaborador es obligatorio");
  if (input.status != null && !PAYSLIP_STATUSES.includes(input.status)) {
    errors.push("El estado de la liquidación no es válido");
  }
  if (input.dailyRate != null && number(input.dailyRate) < 0) {
    errors.push("El tarifa diaria no puede ser negativo");
  }
  return fail(errors);
}

/**
 * ¿Se puede aprobar/pagar esta liquidación? (AgroCloud#38) Una liquidación incompleta
 * NUNCA se aprueba: aprobarla es autorizar un sueldo que nadie sabe si es el
 * correcto.
 */
export function canApprovePayslip(
  payslip: Pick<Payslip, "calculation" | "status">,
): ValidationResult {
  const errors: string[] = [];
  if (!payslip.calculation) {
    errors.push("La liquidación no está calculada");
    return fail(errors);
  }
  if (!payslip.calculation.complete || payslip.calculation.netPay === null) {
    errors.push(
      "La liquidación está incompleta: hay importes que no se pudieron calcular. " +
        "No se puede aprobar un sueldo desconocido.",
    );
  }
  if (payslip.status === "void") errors.push("La liquidación está anulada");
  return fail(errors);
}

// ---------------------------------------------------------------------------
// CONTRATO DE SERVICIO — rutas HTTP del back (torre-de-control-api `payslip`).
// Front y back comparten estas rutas para no divergir.
// ---------------------------------------------------------------------------

export const PAYROLL_ENDPOINTS = {
  // Liquidaciones de sueldo (AgroCloud#38) — entidad principal del módulo.
  payslipsList: "/payslipsList",
  payslip: "/payslip",
  payslipCalc: "/payslipCalc",
  payslipApprove: "/payslipApprove",
  // Timbrado fiscal CFDI (AgroCloud#82)
  payslipStamp: "/payslipStamp",
  payslipReceipt: "/payslipReceipt",
  // Asistencia (AgroCloud#36)
  payrollAttendanceList: "/payrollAttendanceList",
  payrollAttendance: "/payrollAttendance",
  payrollAttendanceSummary: "/payrollAttendanceSummary",
  // Anticipos y otros haberes/descuentos (AgroCloud#37)
  payrollAdjustmentList: "/payrollAdjustmentList",
  payrollAdjustment: "/payrollAdjustment",
  // Incidencias del equipo y su autorización (AgroCloud#81)
  payrollWorkLogList: "/payrollWorkLogList",
  payrollWorkLog: "/payrollWorkLog",
  payrollWorkLogTransition: "/payrollWorkLogTransition",
  // Parámetros previsionales (AgroCloud#38)
  payrollContributionList: "/payrollContributionList",
  payrollContribution: "/payrollContribution",
  // Periodos de nómina y su cierre (AgroCloud#82)
  payrollPeriodList: "/payrollPeriodList",
  payrollPeriod: "/payrollPeriod",
  payrollPeriodClose: "/payrollPeriodClose",
} as const;

export type PayrollEndpoint = (typeof PAYROLL_ENDPOINTS)[keyof typeof PAYROLL_ENDPOINTS];

/** Versión del contrato, útil para trazas/diagnóstico. */
export const TORRE_PAYROLL_VERSION = "0.1.0";
